import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Terminal, Volume2, Film, Gift, Clock, Hash, CornerDownLeft, ArrowRight } from "lucide-react";
import { api } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import { cn } from "@/lib/utils";
import { scoreSearch } from "@/lib/search";
import { isVideoUrl } from "@/lib/upload";
import { HOME_ITEM, NAV_GROUPS, TAB_PAGES } from "@/components/layout/nav";
const EMPTY = { commands: [], sounds: [], rewards: [], timers: [], counters: [] };
const PER_GROUP = 5;
/** Content is fetched when the search opens; within this window it reuses the last fetch. */
const STALE_MS = 30_000;
const PAGES = [
    { ...HOME_ITEM, group: "" },
    ...NAV_GROUPS.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label }))),
    ...TAB_PAGES,
];
/** One kind of content: how to score an item and what its row looks like */
function contentGroup(label, page, query, items, row) {
    const hits = items
        .map((item) => {
        const r = row(item);
        return { r, score: scoreSearch(query, r.name ?? r.title, [r.title, ...r.extra]) };
    })
        .filter((h) => h.score > 0)
        .sort((a, b) => b.score - a.score);
    if (hits.length === 0)
        return null;
    const results = hits.slice(0, PER_GROUP).map(({ r }) => ({
        key: `${label}:${r.key}`,
        title: r.title,
        subtitle: r.subtitle,
        icon: r.icon,
        to: `${page}?q=${encodeURIComponent(r.filter)}`,
    }));
    if (hits.length > PER_GROUP) {
        results.push({
            key: `${label}:more`,
            title: `Alle ${hits.length} Treffer in ${label}`,
            icon: ArrowRight,
            to: `${page}?q=${encodeURIComponent(query.trim())}`,
            more: true,
        });
    }
    return { label, results, best: hits[0].score };
}
export function GlobalSearch({ open, onClose }) {
    const channel = useAuthStore((s) => s.activeChannel);
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const [content, setContent] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const fetched = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        setQuery("");
        setActive(0);
        // Focus after the panel is in the DOM (it isn't rendered while closed).
        requestAnimationFrame(() => inputRef.current?.focus());
        if (!channel)
            return;
        const last = fetched.current;
        if (last && last.channelId === channel.id && Date.now() - last.at < STALE_MS)
            return;
        if (last?.channelId !== channel.id)
            setContent(EMPTY);
        // GET only. The Kanalpunkte page itself imports from Twitch on load; the
        // search must not trigger that, so it reads the stored rewards instead.
        const cid = channel.id;
        const get = async (path) => {
            try {
                const res = await api.get(`/channels/${cid}/${path}`);
                return Array.isArray(res.data) ? res.data : [];
            }
            catch {
                return [];
            }
        };
        setLoading(true);
        Promise.all([
            get("commands"),
            get("sounds"),
            get("channelpoints"),
            get("timers"),
            get("counters"),
        ]).then(([commands, sounds, rewards, timers, counters]) => {
            setLoading(false);
            if (useAuthStore.getState().activeChannel?.id !== cid)
                return;
            setContent({ commands, sounds, rewards, timers, counters });
            fetched.current = { channelId: cid, at: Date.now() };
        });
    }, [open, channel?.id]);
    const groups = useMemo(() => {
        const pageHits = PAGES.map((p) => ({ p, score: scoreSearch(query, p.label, [p.group, p.keywords]) }))
            .filter((h) => h.score > 0)
            .sort((a, b) => b.score - a.score);
        const pages = pageHits.length
            ? {
                label: "Seiten",
                best: pageHits[0].score,
                // Without a query this is the whole menu, as a quick way to jump.
                results: (query.trim() ? pageHits.slice(0, 6) : pageHits).map(({ p }) => ({
                    key: `page:${p.to}`,
                    title: p.label,
                    subtitle: p.group || undefined,
                    icon: p.icon,
                    to: p.to,
                    external: p.external,
                })),
            }
            : null;
        if (!query.trim())
            return pages ? [pages] : [];
        const found = [
            contentGroup("Befehle", "/commands", query, content.commands, (c) => ({
                key: c.id,
                title: `!${c.trigger}`,
                filter: c.trigger,
                extra: [c.aliases.join(" "), c.response],
                subtitle: c.response,
                icon: Terminal,
            })),
            contentGroup("Sounds & Videos", "/sounds", query, content.sounds, (s) => ({
                key: s.id,
                title: `!sound ${s.name}`,
                name: s.name,
                filter: s.name,
                extra: [s.fileUrl?.split("/").pop()],
                subtitle: s.fileUrl && isVideoUrl(s.fileUrl) ? "Video" : "Sound",
                icon: s.fileUrl && isVideoUrl(s.fileUrl) ? Film : Volume2,
            })),
            contentGroup("Kanalpunkte", "/channelpoints", query, content.rewards, (r) => ({
                key: r.id,
                title: r.rewardTitle,
                filter: r.rewardTitle,
                extra: [r.prompt],
                subtitle: `${r.cost} Punkte${r.isSynced ? " · auf Twitch" : ""}`,
                icon: Gift,
            })),
            contentGroup("Timer", "/timers", query, content.timers, (t) => ({
                key: t.id,
                title: t.name,
                filter: t.name,
                extra: [t.message],
                subtitle: t.message,
                icon: Clock,
            })),
            contentGroup("Zähler", "/counters", query, content.counters, (c) => ({
                key: c.id,
                title: c.name,
                filter: c.name,
                extra: [],
                subtitle: `Stand ${c.value}`,
                icon: Hash,
            })),
        ].filter((g) => !!g);
        // Pages first only when one is a clear hit by name; otherwise content
        // with the best match leads ("airhorn" should not open with menu entries).
        const all = pages ? [pages, ...found] : found;
        return all.sort((a, b) => b.best - a.best);
    }, [query, content]);
    const flat = groups.flatMap((g) => g.results);
    useEffect(() => setActive(0), [query]);
    useEffect(() => {
        listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
    }, [active]);
    if (!open)
        return null;
    const pick = (r) => {
        onClose();
        if (r.external)
            window.open(r.to, "_blank", "noreferrer");
        else
            navigate(r.to);
    };
    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, flat.length - 1));
        }
        else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        }
        else if (e.key === "Enter") {
            e.preventDefault();
            if (flat[active])
                pick(flat[active]);
        }
        else if (e.key === "Escape") {
            e.preventDefault();
            onClose();
        }
    };
    let idx = -1;
    return (_jsx("div", { className: "fixed inset-0 z-[60] flex items-start justify-center bg-black/60 p-3 pt-[8vh] sm:p-4 sm:pt-[12vh]", onMouseDown: (e) => e.target === e.currentTarget && onClose(), role: "dialog", "aria-modal": "true", "aria-label": "Suche", children: _jsxs("div", { className: "flex max-h-[80dvh] w-full max-w-xl flex-col overflow-hidden rounded-xl border bg-card text-card-foreground shadow-2xl", children: [_jsxs("div", { className: "flex items-center gap-3 border-b px-4", children: [_jsx(Search, { className: "h-4 w-4 shrink-0 text-muted-foreground" }), _jsx("input", { ref: inputRef, value: query, onChange: (e) => setQuery(e.target.value), onKeyDown: onKeyDown, placeholder: "Seiten, Befehle, Sounds, Kanalpunkte \u2026", className: "h-12 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground sm:text-sm", "aria-label": "Suchbegriff", "aria-controls": "global-search-results", "aria-activedescendant": flat[active] ? `gs-${active}` : undefined, autoComplete: "off", spellCheck: false }), _jsx("button", { onClick: onClose, className: "shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground hover:text-foreground", "aria-label": "Suche schlie\u00DFen", children: "ESC" })] }), _jsxs("div", { ref: listRef, id: "global-search-results", role: "listbox", className: "flex-1 overflow-y-auto p-2", children: [groups.map((g) => (_jsxs("div", { className: "mb-2 last:mb-0", children: [_jsx("div", { className: "px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground", children: g.label }), g.results.map((r) => {
                                    idx += 1;
                                    const i = idx;
                                    const Icon = r.icon;
                                    return (_jsxs("button", { id: `gs-${i}`, "data-idx": i, role: "option", "aria-selected": i === active, onMouseMove: () => i !== active && setActive(i), onClick: () => pick(r), className: cn("flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm", i === active ? "bg-accent text-accent-foreground" : "text-foreground", r.more && "text-muted-foreground"), children: [_jsx(Icon, { className: cn("h-4 w-4 shrink-0", r.more ? "" : "text-muted-foreground") }), _jsxs("span", { className: "min-w-0 flex-1", children: [_jsx("span", { className: "block truncate font-medium", children: r.title }), r.subtitle && _jsx("span", { className: "block truncate text-xs text-muted-foreground", children: r.subtitle })] }), i === active && _jsx(CornerDownLeft, { className: "hidden h-3.5 w-3.5 shrink-0 text-muted-foreground sm:block" })] }, r.key));
                                })] }, g.label))), query.trim() && flat.length === 0 && (_jsx("p", { className: "px-2 py-8 text-center text-sm text-muted-foreground", children: loading ? "Lädt …" : `Nichts gefunden für „${query.trim()}“.` }))] }), _jsxs("div", { className: "hidden items-center gap-4 border-t px-4 py-2 text-[11px] text-muted-foreground sm:flex", children: [_jsxs("span", { children: [_jsx("kbd", { className: "font-mono", children: "\u2191 \u2193" }), " ausw\u00E4hlen"] }), _jsxs("span", { children: [_jsx("kbd", { className: "font-mono", children: "Enter" }), " \u00F6ffnen"] }), _jsxs("span", { className: "ml-auto", children: [_jsx("kbd", { className: "font-mono", children: "Strg K" }), " oder ", _jsx("kbd", { className: "font-mono", children: "/" }), " \u00F6ffnet die Suche"] })] })] }) }));
}
